import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Uuid } from '@/api/types';
import type { OrganType } from '@/api/apis/case';

export interface CaseTabType {
  id: Uuid;
  donorId: string;
  caseSuffixes: string;
  organType: OrganType;
  lastVisitedPath?: string;
}

interface CaseTabsState {
  tabsList: CaseTabType[];
  removeTab: (id: string) => void;
  addTab: (data: CaseTabType) => void;
  patchTab: (id: Uuid, patch: Partial<CaseTabType>) => void;
  reorderTab: (draggedId: string, targetId: string) => void;
}

interface CreateCaseTabsStoreConfig {
  storageKey: string;
  version?: number;
}

const getDefaultLastVisitedPath = (id: Uuid) => `/case/${id}/details`;

export const createCaseTabsStore = ({ storageKey, version = 1 }: CreateCaseTabsStoreConfig) => {
  return create<CaseTabsState>()(
    persist(
      (set) => ({
        tabsList: [],

        addTab: (data) =>
          set((state) => {
            if (state.tabsList.some((tab) => tab.id === data.id)) {
              return state;
            }

            return {
              tabsList: [
                ...state.tabsList,
                {
                  ...data,
                  lastVisitedPath: data.lastVisitedPath ?? getDefaultLastVisitedPath(data.id),
                },
              ],
            };
          }),

        patchTab: (id, patch) =>
          set((state) => ({
            tabsList: state.tabsList.map((tab) =>
              tab.id === id
                ? {
                    ...tab,
                    ...patch,
                    lastVisitedPath: patch.lastVisitedPath ?? tab.lastVisitedPath ?? getDefaultLastVisitedPath(id),
                  }
                : tab,
            ),
          })),

        removeTab: (id) =>
          set((state) => ({
            tabsList: state.tabsList.filter((tab) => tab.id !== id),
          })),

        reorderTab: (draggedId, targetId) =>
          set((state) => {
            if (draggedId === targetId) {
              return state;
            }

            const draggedIndex = state.tabsList.findIndex((tab) => tab.id === draggedId);
            const targetIndex = state.tabsList.findIndex((tab) => tab.id === targetId);

            if (draggedIndex === -1 || targetIndex === -1) {
              return state;
            }

            const nextTabsList = [...state.tabsList];
            const [draggedTab] = nextTabsList.splice(draggedIndex, 1);

            nextTabsList.splice(targetIndex, 0, draggedTab);

            return {
              tabsList: nextTabsList,
            };
          }),
      }),
      {
        name: storageKey,
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({ tabsList: state.tabsList }),
        version,
      },
    ),
  );
};
